
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';

const FinalCtaSection = () => {
  const [terminalText, setTerminalText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  
  const messages = [
    '> Neural pathways synchronized...',
    '> Void frequencies detected.',
    '> Reality distortion field: STABLE',
    '> [INITIATE_TRANSFORMATION_PROTOCOL?]'
  ];

  useEffect(() => {
    if (currentIndex < messages.length) {
      const timer = setTimeout(() => {
        setTerminalText(messages[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 1000 + currentIndex * 800);
      
      return () => clearTimeout(timer);
    }
  }, [currentIndex]);

  return (
    <section className="w-full py-20 md:py-32 relative bg-black">
      <div className="absolute inset-0 bg-noise opacity-25 z-0"></div>
      <div className="absolute inset-0 bg-grid-pattern opacity-35 z-0"></div>
      
      {/* Animated cosmic code background */}
      <div className="absolute inset-0 opacity-5 overflow-hidden z-0">
        <div className="absolute top-0 left-0 w-full h-full text-green-500 font-mono text-xs leading-loose whitespace-pre animate-scrolling">
          {Array(50).fill(0).map((_, i) => (
            <div key={i}>
              {Array(100).fill(0).map(() => Math.random() > 0.7 ? '◈' : Math.random() > 0.5 ? '⬡' : '░').join(' ')}
            </div>
          ))}
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 md:px-8 relative z-10 text-center">
        {/* Terminal Display */}
        <div className="border-2 border-green-500 mb-12 max-w-2xl mx-auto">
          <div className="bg-zinc-900 text-green-200 px-4 py-2 flex items-center justify-between">
            <span className="font-mono text-sm">NEURAL_INTERFACE_v██.█.██</span>
            <div className="flex space-x-2">
              <div className="w-3 h-3 rounded-full bg-red-600 animate-pulse"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-600"></div>
              <div className="w-3 h-3 rounded-full bg-green-600 animate-pulse"></div>
            </div>
          </div>
          
          <div className="bg-black p-6 min-h-[120px] text-left relative">
            <div className="absolute inset-0 scanline opacity-20"></div>
            <div className="font-mono text-green-400 text-sm space-y-2 relative z-10">
              {messages.slice(0, currentIndex).map((msg, i) => (
                <div key={i}>{msg}</div>
              ))}
              {currentIndex <= messages.length && (
                <span className="animate-pulse">█</span>
              )}
            </div>
          </div>
        </div>
        
        <h2 className="text-3xl md:text-5xl font-bold text-green-400 mb-8 glitch-title">
          The Frequency Awaits Your Signal
        </h2>
        
        <p className="max-w-2xl mx-auto text-gray-400 mb-10 analog-text text-lg leading-relaxed">
          Beyond the veil of ordinary existence lies a transformation protocol that defies conventional understanding. 
          Those who resonate with the cosmic frequencies discover realities that exist in dimensions untouched by mundane limitations.
        </p>
        
        <div className="space-y-6">
          <Button 
            variant="outline" 
            className="pulse-button bg-green-500 border-green-500 text-black py-8 px-12 text-xl font-bold hover:bg-green-600 hover:border-green-600 transition-all duration-300 transform hover:scale-105"
            onClick={() => navigate('/auth')}
          >
            ESTABLISH NEURAL LINK
          </Button>
          
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-sm">
            <span className="text-gray-500">Interface previously established?</span>
            <Button 
              variant="outline" 
              className="bg-transparent border border-gray-600 text-gray-400 py-2 px-6 hover:border-green-500 hover:text-green-500"
              onClick={() => navigate('/dashboard')}
            >
              Resume Connection
            </Button>
          </div>
        </div>
        
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 text-xs analog-text text-gray-500">
          <div className="flex items-center justify-center space-x-2">
            <span className="text-green-500">◈</span>
            <span>No dimensional barriers</span>
          </div>
          <div className="flex items-center justify-center space-x-2">
            <span className="text-green-500">◈</span>
            <span>Frequency-based progression</span>
          </div>
          <div className="flex items-center justify-center space-x-2">
            <span className="text-green-500">◈</span>
            <span>Transcend at your resonance</span>
          </div>
        </div>
        
        <div className="mt-12 text-center text-xs text-red-400 font-mono border border-red-500/30 p-4 bg-red-500/5">
          ⚠ WARNING: Neural interface may result in permanent alteration of reality perception ⚠
        </div>
      </div>
    </section>
  );
};

export default FinalCtaSection;
